import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { colors } from '../theme';
import { fadeInUp } from '../utils/animations';

/**
 * HEURÍSTICAS APLICADAS NO HERO CAROUSEL:
 *
 * #1 - Visibilidade do status do sistema:
 *   - Indicadores de slide ativos
 *   - Barra de progresso mostra tempo restante
 *   - Feedback visual ao trocar slides
 *
 * #2 - Correspondência entre sistema e mundo real:
 *   - Navegação familiar de carousel
 *   - CTA claro e direto
 *
 * #3 - Controle e liberdade do usuário:
 *   - Usuário pode navegar manualmente pelos slides
 *   - Pode pausar ao interagir
 *
 * #4 - Consistência e padrões:
 *   - Transições suaves e previsíveis
 *   - Indicadores padrão de carousel
 *
 * #8 - Design estético e minimalista:
 *   - Transições elegantes sem distrações
 */

interface Slide {
  headline: string;
  subheadline: string;
  ctaText: string;
  image?: string;
}

const slides: Slide[] = [
  {
    headline: 'Cuidado de Respeito',
    subheadline: 'Produtos sem frescura que levam o ofício a sério',
    ctaText: 'Quero ter Barba Barão na minha barbearia',
    image: 'https://images.unsplash.com/photo-1503951914875-452162b0f3f1?q=80&w=2000&auto=format&fit=crop',
  },
  {
    headline: 'Barba Forte, Presença Marcante',
    subheadline: 'Qualidade profissional, resultados visíveis',
    ctaText: 'Quero ter Barba Barão na minha barbearia',
    image: 'https://images.unsplash.com/photo-1621605815971-fbc98d665033?q=80&w=2000&auto=format&fit=crop',
  },
  {
    headline: 'Tradição que Transforma',
    subheadline: 'Do clássico ao contemporâneo, sempre com excelência',
    ctaText: 'Quero ter Barba Barão na minha barbearia',
    image: '',
  },
];

const HeroCarousel: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setDirection(1);
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused]);

  const handleCTAClick = () => {
    const partnersSection = document.querySelector('#parceiros');
    if (partnersSection) {
      partnersSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Heurística #3: Controle do usuário - navegar manualmente
  const handleSlideClick = (index: number) => {
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  };

  const currentSlideData = slides[currentSlide];
  const hasImage = currentSlideData.image && currentSlideData.image.trim() !== '';

  // Animações do slide
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -100 : 100,
      opacity: 0,
    }),
  };

  return (
    <Box
      id="inicio"
      sx={{
        position: 'relative',
        height: { xs: '70vh', md: '80vh' },
        minHeight: '600px',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        backgroundColor: hasImage ? colors.blackMatte : colors.darkWine,
        backgroundImage: hasImage ? `url("${currentSlideData.image}")` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: hasImage
            ? `
              linear-gradient(
                135deg,
                rgba(26, 26, 26, 0.2) 0%,
                rgba(110, 46, 30, 0.4) 50%,
                rgba(26, 26, 26, 0.2) 100%
              )
            `
            : `
              linear-gradient(
                135deg,
                rgba(26, 26, 26, 0.3) 0%,
                rgba(110, 46, 30, 0.2) 50%,
                rgba(26, 26, 26, 0.3) 100%
              )
            `,
          zIndex: 1,
        },
        '&:after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9B18C' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          opacity: 0.2,
          zIndex: 2,
        },
      }}
      // Heurística #3: Pausar ao hover
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3 }}>
        <Box
          sx={{
            textAlign: 'center',
            color: colors.beige,
          }}
        >
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentSlide}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: 'tween', duration: 0.6, ease: [0.6, 0.01, 0.05, 0.95] },
                opacity: { duration: 0.4 },
              }}
            >
              <motion.div variants={fadeInUp} initial="hidden" animate="visible">
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    mb: 3,
                    color: colors.beige,
                    textShadow: '2px 2px 8px rgba(0, 0, 0, 0.7)',
                    fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                  }}
                >
                  {currentSlideData.headline}
                </Typography>

                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    mb: 5,
                    color: colors.beige,
                    textShadow: '1px 1px 4px rgba(0, 0, 0, 0.7)',
                    maxWidth: '800px',
                    mx: 'auto',
                    fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                    fontWeight: 400,
                    opacity: 0.95,
                  }}
                >
                  {currentSlideData.subheadline}
                </Typography>

                <Button
                  component={motion.button}
                  whileHover={{ scale: 1.05, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  variant="contained"
                  size="large"
                  onClick={handleCTAClick}
                  aria-label={currentSlideData.ctaText}
                  sx={{
                    px: { xs: 3, md: 5 },
                    py: { xs: 1.5, md: 2 },
                    fontSize: { xs: '0.9rem', md: '1.1rem' },
                    backgroundColor: colors.darkWine,
                    color: colors.beige,
                    border: `2px solid ${colors.agedGold}`,
                    fontWeight: 700,
                    transition: 'all 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
                  }}
                >
                  {currentSlideData.ctaText}
                </Button>
              </motion.div>
            </motion.div>
          </AnimatePresence>

          {/* Heurística #1: Indicadores de slide com progresso */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              mt: 6,
            }}
          >
            {slides.map((_, index) => (
              <Box
                key={index}
                onClick={() => handleSlideClick(index)}
                sx={{
                  position: 'relative',
                  width: currentSlide === index ? '50px' : '14px',
                  height: '14px',
                  borderRadius: '7px',
                  backgroundColor: colors.beige,
                  opacity: currentSlide === index ? 1 : 0.4,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
                  '&:hover': {
                    opacity: 1,
                    transform: 'scale(1.1)',
                  },
                }}
                role="button"
                aria-label={`Ir para slide ${index + 1}`}
                tabIndex={0}
              >
                {/* Heurística #1: Barra de progresso do slide ativo */}
                {currentSlide === index && !isPaused && (
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{
                      duration: 5,
                      ease: 'linear',
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: colors.agedGold,
                      transformOrigin: 'left',
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroCarousel;
